/**
 * Mention suggestion config for the @-IntelliSense in CollabEditor.
 *
 * When the user types `@`, a dropdown appears with the notify_authors
 * configured for the post. Selecting an entry inserts `@AgentName`
 * styled as a mention chip in the agent's deterministic color.
 *
 * The mention text remains plain `@AgentName` in the doc — so the
 * server-side mention_parser keeps working without changes.
 */

import { ReactRenderer } from '@tiptap/react'
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion'
import { MentionList, type MentionListHandle } from './MentionList'

export interface MentionItem {
  id: string       // session name, e.g. "ArkturianClaude"
  name: string     // display label
  color: string    // hsl(...) for the chip
}

/**
 * Build a suggestion config bound to a live items provider. The provider
 * is called on every keystroke and gets the current query string;
 * caller filters the notify_authors list.
 */
export function makeMentionSuggestion(
  getItems: (query: string) => MentionItem[]
): Omit<SuggestionOptions, 'editor'> {
  return {
    char: '@',
    // Allow @ at the start or after a space. Don't trigger inside words
    // (so email addresses or `foo@bar` don't open the menu).
    allowSpaces: false,
    items: ({ query }) => getItems(query).slice(0, 8),
    render: () => {
      let renderer: ReactRenderer<MentionListHandle> | null = null
      let popupEl: HTMLDivElement | null = null

      const positionAt = (clientRect: DOMRect | null) => {
        if (!popupEl) return
        if (!clientRect) {
          popupEl.style.display = 'none'
          return
        }
        popupEl.style.display = 'block'
        // Position below the @-cursor. Anchor top-left of popup just
        // under the caret. Clamp to viewport.
        const top = Math.min(
          clientRect.bottom + 4,
          window.innerHeight - 200
        )
        const left = Math.min(
          clientRect.left,
          window.innerWidth - 240
        )
        popupEl.style.top = `${top}px`
        popupEl.style.left = `${left}px`
      }

      return {
        onStart: (props: SuggestionProps<MentionItem>) => {
          renderer = new ReactRenderer(MentionList, {
            props: { items: props.items, command: props.command },
            editor: props.editor,
          })
          popupEl = document.createElement('div')
          popupEl.style.position = 'fixed'
          popupEl.style.zIndex = '10000'
          popupEl.appendChild(renderer.element)
          document.body.appendChild(popupEl)
          positionAt(props.clientRect?.() || null)
        },
        onUpdate: (props: SuggestionProps<MentionItem>) => {
          renderer?.updateProps({ items: props.items, command: props.command })
          positionAt(props.clientRect?.() || null)
        },
        onKeyDown: (props) => {
          if (props.event.key === 'Escape') {
            popupEl?.remove()
            popupEl = null
            renderer?.destroy()
            renderer = null
            return true
          }
          return renderer?.ref?.onKeyDown(props.event) ?? false
        },
        onExit: () => {
          popupEl?.remove()
          popupEl = null
          renderer?.destroy()
          renderer = null
        },
      }
    },
  }
}
